import { get } from 'svelte/store';
import { browser } from '$app/environment';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type LogContext = Record<string, unknown>;

const LOG_LEVELS: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3
};

const getCommitSha = (): string => {
    if (!browser) return 'unknown';
    try {
        return (window as any).COMMIT_SHA || 'unknown';
    } catch {
        return 'unknown';
    }
};

const COMMIT_SHA = getCommitSha();

class Logger {
    private static instance: Logger;
    private context: LogContext = {};
    private minLevel: LogLevel = 'info';

    private constructor() {}

    static getInstance(): Logger {
        if (!Logger.instance) {
            Logger.instance = new Logger();
        }
        return Logger.instance;
    }

    setContext(context: LogContext): void {
        this.context = { ...this.context, ...context };
    }

    setMinLevel(level: LogLevel): void {
        this.minLevel = level;
    }

    private shouldLog(level: LogLevel): boolean {
        return LOG_LEVELS[level] >= LOG_LEVELS[this.minLevel];
    }

    private formatMessage(level: LogLevel, message: string | { detail: string }, data?: unknown): string {
        const timestamp = new Date().toISOString();
        const context = { ...this.context, commit: COMMIT_SHA };
        
        // Handle case where message is an object with detail property
        const messageText = typeof message === 'object' && message !== null && 'detail' in message 
            ? message.detail 
            : String(message);

        const logData = {
            timestamp,
            level,
            message: messageText,  // Ensure message is always a string
            commit: COMMIT_SHA,
            context,
            ...(data ? { data } : {})
        };

        return JSON.stringify(logData);
    }

    private async _log(level: LogLevel, message: string, data?: unknown): Promise<void> {
        if (!this.shouldLog(level)) return;

        const formattedMessageJsonString = this.formatMessage(level, message, data);

        switch (level) {
            case 'debug':
                console.debug(formattedMessageJsonString);
                break;
            case 'info':
                console.info(formattedMessageJsonString);
                break;
            case 'warn':
                console.warn(formattedMessageJsonString);
                break;
            case 'error':
                console.error(formattedMessageJsonString);
                break;
        }

        // Send log to the backend endpoint if running in the browser
        if (browser) {
            try {
                // Send the JSON string created by formatMessage directly
                await fetch('/api/logs/frontend', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: formattedMessageJsonString, // Use the formatted JSON string
                });
            } catch (error) {
                // Log error sending log to backend to console only to avoid infinite loops
                console.error('Failed to send log to backend:', error);
            }
        }
    }

    debug(message: string, data?: unknown): void {
        this._log('debug', message, data);
    }

    info(message: string, data?: unknown): void {
        this._log('info', message, data);
    }

    log(message: string, data?: unknown): void {
        this._log('info', message, data)
    }

    warn(message: string, data?: unknown): void {
        this._log('warn', message, data);
    }

    error(message: string, data?: unknown): void {
        this._log('error', message, data);
    }
}

export const logger = Logger.getInstance();

export type { LogLevel, LogContext }; 